import type {
  AudienceData,
  InterestsAudienceData,
} from "../interfaces/KYDMarketing";

export type InterestKeyValue = { key: string; value: string };

export type InterestSearchResult = { id: string; name: string };

const INTERESTS_NAME_SAMPLE_MAX = 4;

/** Meta `/search?type=adinterest` and API gateway `{ response: … }` shapes. */
export function normalizeMetaInterestSearchResponse(
  raw: unknown
): { data: InterestSearchResult[] } {
  const payload =
    raw &&
    typeof raw === "object" &&
    "response" in (raw as object) &&
    (raw as { response?: unknown }).response != null
      ? (raw as { response: unknown }).response
      : raw;

  if (!payload || typeof payload !== "object") {
    return { data: [] };
  }

  const obj = payload as Record<string, unknown>;
  if (obj.error && typeof obj.error === "object") {
    const message =
      typeof (obj.error as { message?: unknown }).message === "string"
        ? (obj.error as { message: string }).message
        : "Interest search failed";
    throw new Error(message);
  }

  const items = Array.isArray(obj.data)
    ? obj.data
    : Array.isArray(payload)
      ? payload
      : [];

  const data: InterestSearchResult[] = [];
  for (const item of items) {
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const id =
      row.id != null ? String(row.id).trim() : row.key != null ? String(row.key).trim() : "";
    const name = typeof row.name === "string" ? row.name.trim() : "";
    if (!id || !name) continue;
    data.push({ id, name });
  }

  return { data };
}

export function isInterestsAudience(
  audience: AudienceData
): audience is InterestsAudienceData {
  return audience.audience_type === "interests";
}

export function partitionAudienceData(audiences: AudienceData[] | undefined): {
  metaCustomAudiences: AudienceData[];
  interestAudiences: InterestsAudienceData[];
} {
  const list = audiences ?? [];
  const metaCustomAudiences: AudienceData[] = [];
  const interestAudiences: InterestsAudienceData[] = [];
  for (const audience of list) {
    if (isInterestsAudience(audience)) {
      interestAudiences.push(audience);
    } else {
      metaCustomAudiences.push(audience);
    }
  }
  return { metaCustomAudiences, interestAudiences };
}

export function mergeInterestAudiences(
  interestAudiences: InterestsAudienceData[]
): InterestKeyValue[] {
  const byKey = new Map<string, InterestKeyValue>();
  for (const bundle of interestAudiences) {
    for (const interest of bundle.interests ?? []) {
      const key = interest.key?.trim();
      if (!key) continue;
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          value: interest.value?.trim() || key,
        });
      }
    }
  }
  return Array.from(byKey.values());
}

/** Display name for an interests bundle (no "Interests:" prefix; badge carries type). */
export function formatInterestsAudienceName(
  interests: readonly InterestKeyValue[]
): string {
  if (interests.length === 0) return "Interests";
  const labels = interests.map((i) => i.value || i.key);
  const shown = labels.slice(0, INTERESTS_NAME_SAMPLE_MAX);
  const tail =
    labels.length > INTERESTS_NAME_SAMPLE_MAX
      ? `${shown.join(", ")} and more`
      : shown.join(", ");
  return tail;
}

export function countInterestTargetingRows(
  custom_audiences?: readonly AudienceData[] | null,
  flatInterests?: readonly unknown[] | null
): number {
  const bundles =
    custom_audiences?.filter(isInterestsAudience).length ?? 0;
  if (bundles > 0) return bundles;
  return (flatInterests?.length ?? 0) > 0 ? 1 : 0;
}

export function hasInterestTargetingSignal(params: {
  custom_audiences?: readonly AudienceData[] | null;
  interests?: readonly unknown[] | null;
}): boolean {
  return countInterestTargetingRows(
    params.custom_audiences,
    params.interests
  ) > 0;
}

/** Union bundles + optional legacy flat interests (deduped by key). */
export function synthesizeInterestsAudienceFromFlat(
  flat: readonly InterestKeyValue[],
  id?: string
): InterestsAudienceData {
  const interests = flat.map((i) => ({
    key: i.key,
    value: i.value || i.key,
  }));
  return {
    ...(id ? { id } : {}),
    audience_type: "interests",
    interests,
    name: formatInterestsAudienceName(interests),
    created_at: new Date().toISOString(),
  };
}

/** Show legacy flat `interests` as one bundle when no persisted interest audiences exist. */
export function hydrateCustomAudiencesWithInterests(params: {
  custom_audiences?: readonly AudienceData[] | null;
  flatInterests?: readonly InterestKeyValue[] | null;
}): AudienceData[] {
  const audiences = [...(params.custom_audiences ?? [])];
  if (audiences.some(isInterestsAudience)) return audiences;
  const flat = params.flatInterests ?? [];
  if (flat.length === 0) return audiences;
  return [...audiences, synthesizeInterestsAudienceFromFlat(flat)];
}

export function interestsAudienceIdentityKey(
  audience: InterestsAudienceData
): string {
  const id = (audience.id ?? "").trim();
  if (id) return `id:${id}`;
  const keys = (audience.interests ?? [])
    .map((i) => i.key?.trim())
    .filter(Boolean)
    .sort()
    .join(",");
  return keys ? `keys:${keys}` : "";
}

/** Stable id seed for interests bundles when mapping wire rows to persisted `MarketingCustomAudience`. */
export function interestsAudiencePersistIdSeed(
  audience: InterestsAudienceData
): string {
  const identity = interestsAudienceIdentityKey(audience);
  if (identity.startsWith("keys:")) {
    return `interests:${identity.slice("keys:".length)}`;
  }
  const name = (audience.name ?? "").trim();
  if (name) return `interests:name:${name}`;
  const created = (audience.created_at ?? "").trim();
  return created ? `interests:created:${created}` : "interests:bundle";
}

const INTERESTS_WIRE_CLIENT_ID_PREFIX = "interests_audience_";

export function isStableInterestsWireAudienceId(id: string | undefined): boolean {
  const v = (id ?? "").trim();
  return (
    v.startsWith("CD") ||
    v.startsWith(INTERESTS_WIRE_CLIENT_ID_PREFIX)
  );
}

/**
 * Index of the interests bundle to replace when the user edits one in the guided
 * audience picker. Returns -1 when the flow should append (create).
 */
export function findInterestsAudienceIndexToReplace(
  audiences: readonly AudienceData[],
  editTarget: InterestsAudienceData
): number {
  const targetKey = interestsAudienceIdentityKey(editTarget);
  if (targetKey) {
    const byKey = audiences.findIndex(
      (a) =>
        isInterestsAudience(a) && interestsAudienceIdentityKey(a) === targetKey
    );
    if (byKey >= 0) return byKey;
  }
  const interestIndices = audiences
    .map((a, i) => (isInterestsAudience(a) ? i : -1))
    .filter((i) => i >= 0);
  if (interestIndices.length === 1) return interestIndices[0];
  return -1;
}

export function resolveMergedTargetingInterests(params: {
  custom_audiences?: readonly AudienceData[] | null;
  flatInterests?: readonly InterestKeyValue[] | null;
}): InterestKeyValue[] {
  const { interestAudiences } = partitionAudienceData(
    params.custom_audiences ? [...params.custom_audiences] : []
  );
  const fromBundles = mergeInterestAudiences(interestAudiences);
  if (fromBundles.length > 0) return fromBundles;
  const flat = params.flatInterests ?? [];
  return mergeInterestAudiences(
    flat.length > 0
      ? [
          {
            audience_type: "interests",
            interests: flat.filter(
              (i): i is InterestKeyValue =>
                typeof i?.key === "string" && typeof i?.value === "string"
            ),
          },
        ]
      : []
  );
}

/** Meta `targeting.interests` materialized from interest audience bundles only. */
export function resolveInterestsFromAudienceBundles(
  custom_audiences?: readonly AudienceData[] | null
): InterestKeyValue[] {
  const { interestAudiences } = partitionAudienceData(
    custom_audiences ? [...custom_audiences] : []
  );
  return mergeInterestAudiences(interestAudiences);
}

/**
 * Derives ad-set `targeting.interests` for persist/Meta from wire/persisted audiences.
 * Wire PUT must not use `targeting.interests`; interest bundles live in `custom_audiences`.
 * When the PUT omits `custom_audiences`, legacy flat interests on the saved ad set may hydrate
 * a read-only bundle for one migration read — never from incoming `targeting.interests`.
 */
export function resolveAdsetPersistedInterestsFromAudiences(params: {
  wireCustomAudiences: readonly AudienceData[];
  existingFlatInterests?: readonly InterestKeyValue[] | null;
  /** True when PUT included `targeting.custom_audiences` (including `[]`). */
  customAudiencesExplicit: boolean;
}): InterestKeyValue[] {
  const audiences = params.customAudiencesExplicit
    ? [...params.wireCustomAudiences]
    : hydrateCustomAudiencesWithInterests({
        custom_audiences: params.wireCustomAudiences,
        flatInterests: params.existingFlatInterests,
      });
  return resolveInterestsFromAudienceBundles(audiences);
}
